import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';

export function AtpPage() {
  const { t } = useTranslation();
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items) });

  const check = useMutation({
    mutationFn: () => inventoryApi.atp({ productId, quantity: Number(quantity) }).then((r) => r.items),
    onError: (e) => setError(e?.message || t('common.loadError')),
    onSuccess: () => setError(null),
  });
  const result = check.data;

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!productId || !quantity || Number(quantity) <= 0) { setError(t('atp.error.required')); return; }
    check.mutate();
  }

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('atp.title')}</h1>
          <p className="muted">{t('atp.subtitle')}</p>
        </div>
      </header>

      <form onSubmit={submit} className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: 1, minWidth: 240 }}>
            <span className="field-label">{t('atp.field.product')}</span>
            <select className="input" value={productId} onChange={(e) => setProductId(e.target.value)} required>
              <option value="">-</option>
              {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('atp.field.quantity')}</span>
            <input className="input num" type="number" min="0.0001" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </label>
          <Button variant="primary" type="submit" disabled={check.isPending}>{check.isPending ? `${t('atp.action.check')}…` : t('atp.action.check')}</Button>
        </div>
        {error && <div className="alert danger" style={{ marginTop: 'var(--space-2)' }}>{error}</div>}
      </form>

      {result && (
        <>
          <div className="card-grid">
            <MetricCard label={t('atp.metric.requested')} value={formatNumber(result.requestedQuantity)} />
            <MetricCard label={t('atp.metric.available')} value={formatNumber(result.totalAvailable)} />
            <MetricCard label={t('atp.metric.result')}
              value={<Badge variant={result.deliverable ? 'success' : 'danger'}>{result.deliverable ? t('atp.deliverable') : t('atp.notDeliverable')}</Badge>}
              trendVariant={result.deliverable ? 'success' : 'danger'} />
            {!result.deliverable && <MetricCard label={t('atp.metric.shortfall')} value={formatNumber(result.shortfall)} trendVariant="danger" />}
          </div>

          <div className="settings-divider">{t('atp.byWarehouse')} · {result.productSku} {result.productName}</div>
          <Table
            columns={[
              { key: 'code', label: t('atp.col.warehouse'), render: (r) => <span className="mono">{r.warehouseCode}</span> },
              { key: 'name', label: t('atp.col.name'), render: (r) => r.warehouseName },
              { key: 'available', label: t('atp.col.available'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.available)}</span> },
            ]}
            rows={result.warehouses || []}
            emptyMessage={t('atp.noStock')}
          />
        </>
      )}
    </section>
  );
}
